import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package, PackageCheck, Scan, ChevronDown, ChevronUp, X, Check, Trash2,
  RotateCcw, ShieldCheck, CircleAlert, FileText, User, ShoppingBag
} from 'lucide-react';
import { api } from '../../../api/client';
import { EntityLink } from '../../../components/shared/EntityLink';
import { hiRes, BatchData } from '../shared';
import { StatusBadge } from '../components/StatusBadge';
import { VerificationModal } from '../modals/VerificationModal';
import { ReleasePreviewModal } from '../modals/ReleasePreviewModal';
import { ReleasePdfButtons } from '../components/ReleasePdfButtons';
import { useCan } from '../../../lib/rbac';

export function BatchesTab({ batches, sellerId, onRefresh, guardProblems }: {
  batches: BatchData[]; sellerId: number; onRefresh: () => void; guardProblems: any[];
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canOperate = can('warehouse.operate');
  const canReopen = can('warehouse.reopen_force');
  const canDeleteItem = can('warehouse.delete_item');
  const [expandedBatch, setExpandedBatch] = useState<number | null>(batches[0]?.id || null);
  const [batchItems, setBatchItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [closingBatch, setClosingBatch] = useState<number | null>(null);
  const [verifyData, setVerifyData] = useState<any>(null);
  const [verifyLoading, setVerifyLoading] = useState<number | null>(null);
  const [releasingBatch, setReleasingBatch] = useState<number | null>(null);
  const [closeScanItemId, setCloseScanItemId] = useState<number | null>(null);
  const [closeScanCode, setCloseScanCode] = useState('');
  const [closeScanError, setCloseScanError] = useState('');
  const closeScanRef = useRef<HTMLInputElement>(null);

  const loadItems = useCallback(async (batchId: number) => {
    setLoadingItems(true);
    try {
      const res: any = await api.get(`/warehouse/batch/${batchId}/items?limit=9999`);
      if (res.success) setBatchItems(res.data || []);
    } catch (err) { console.error('Failed to load items', err); }
    finally { setLoadingItems(false); }
  }, []);

  useEffect(() => { if (expandedBatch) loadItems(expandedBatch); }, [expandedBatch, loadItems]);

  const handleCloseBatch = async (batchId: number, batchSellerId: number) => {
    setClosingBatch(batchId);
    try { await api.post('/warehouse/batch/' + batchId + '/close', { seller_id: batchSellerId }); onRefresh(); }
    catch (err) { console.error('Failed to close batch', err); }
    finally { setClosingBatch(null); }
  };

  const handleReopenBatch = async (batchId: number, batchSellerId: number) => {
    try {
      const res: any = await api.post('/warehouse/batch/' + batchId + '/reopen', { seller_id: batchSellerId });
      if (!res?.success) alert(res?.error || 'No se pudo reabrir el lote');
      onRefresh();
    }
    catch (err: any) { console.error('Failed to reopen batch', err); alert(err?.message || 'No se pudo reabrir el lote'); }
  };

  const handleDeleteItem = async (itemId: number) => {
    try { await api.delete('/warehouse/items/' + itemId); if (expandedBatch) loadItems(expandedBatch); onRefresh(); }
    catch (err) { console.error('Failed to delete item', err); }
  };

  const handlePackItem = async (itemId: number) => {
    try { await api.put('/warehouse/items/' + itemId + '/pack', {}); if (expandedBatch) loadItems(expandedBatch); onRefresh(); }
    catch (err) { console.error('Failed to pack item', err); }
  };

  const handleCloseScanFromList = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!closeScanCode.trim() || !expandedBatch) return;
    setCloseScanError('');
    try {
      const batchSellerId = batches.find(b => b.id === expandedBatch)?.seller_id || sellerId;
      const res: any = await api.post('/warehouse/close-scan', {
        code: closeScanCode.trim(), batch_id: expandedBatch, seller_id: batchSellerId,
      });
      if (res.success) {
        setCloseScanItemId(null); setCloseScanCode('');
        loadItems(expandedBatch); onRefresh();
      } else { setCloseScanError(res.error || t('wh.wrongLabel')); }
    } catch (err: any) { setCloseScanError(err.message || t('wh.wrongLabel')); }
  };

  const handleVerifyBatch = async (batchId: number) => {
    setVerifyLoading(batchId);
    try {
      const res: any = await api.get(`/warehouse/batch/${batchId}/verify`);
      if (res.success) setVerifyData(res);
    } catch (err) { console.error('Failed to verify batch', err); }
    finally { setVerifyLoading(null); }
  };

  // 2026-09-10 — depósito con dos correos: liberar abre el pre-chequeo (conteo por correo +
  // bolsas mezcladas) y genera un documento por correo. `releasingBatch` marca el lote en el modal.
  const [previewBatch, setPreviewBatch] = useState<{ id: number; sellerId: number } | null>(null);
  const handleReleaseBatch = (batchId: number, batchSellerId: number) => {
    setReleasingBatch(batchId);
    setPreviewBatch({ id: batchId, sellerId: batchSellerId });
  };

  if (batches.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--color-text-secondary)]">
        <Package size={40} className="mx-auto mb-3 opacity-50" /><p>{t('wh.noBatches')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {verifyData && (
        <VerificationModal data={verifyData} onClose={() => setVerifyData(null)}
          onRefresh={() => handleVerifyBatch(verifyData.batch_id)} />
      )}
      {previewBatch && (
        <ReleasePreviewModal
          batchId={previewBatch.id}
          sellerId={previewBatch.sellerId}
          canForce={canReopen}
          onClose={() => { setPreviewBatch(null); setReleasingBatch(null); }}
          onReleased={() => onRefresh()}
        />
      )}

      {batches.map(batch => (
        <div key={batch.id} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-xl overflow-hidden">
          {/* Batch Header */}
          <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
            onClick={() => setExpandedBatch(expandedBatch === batch.id ? null : batch.id)}>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-[var(--color-text)]">Batch #{batch.id}</span>
                <StatusBadge status={batch.status} />
                <span className="text-sm text-[var(--color-text-secondary)]">{batch.seller.nickname}</span>
              </div>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                {new Date(batch.batch_date).toLocaleDateString()}
                {batch.opened_at && ` -- ${t('wh.opened')}: ${new Date(batch.opened_at).toLocaleTimeString()}`}
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {[
                { n: batch.counts.total, l: t('common.total'), c: 'text-[var(--color-text)]' },
                { n: batch.counts.received, l: t('wh.received'), c: 'text-yellow-600' },
                { n: batch.counts.packed, l: t('wh.packed'), c: 'text-blue-600' },
                { n: batch.counts.closed, l: t('wh.closed'), c: 'text-green-600' },
              ].map(({ n, l, c }) => (
                <div key={l} className="text-center">
                  <div className={`text-lg font-bold ${c}`}>{n}</div>
                  <div className="text-xs text-[var(--color-text-secondary)]">{l}</div>
                </div>
              ))}
              {batch.counts.alerts > 0 && (
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{batch.counts.alerts}</div>
                  <div className="text-xs text-red-500">{t('wh.alerts')}</div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              {batch.status === 'open' && canOperate && (
                <button onClick={() => handleCloseBatch(batch.id, batch.seller_id)} disabled={closingBatch === batch.id}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {closingBatch === batch.id ? '...' : t('wh.closeBatch')}
                </button>
              )}
              {batch.status === 'closed' && canOperate && (
                <button onClick={() => handleReleaseBatch(batch.id, batch.seller_id)} disabled={releasingBatch === batch.id}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                  <FileText size={12} />{releasingBatch === batch.id ? '...' : t('wh.release')}
                </button>
              )}
              {batch.status === 'released' && canOperate && (
                <ReleasePdfButtons batchId={batch.id} sellerId={batch.seller_id} fallbackUrl={batch.packing_release_url} />
              )}
              {(batch.status === 'closed' || batch.status === 'released') && canOperate && (
                <button onClick={() => handleVerifyBatch(batch.id)} disabled={verifyLoading === batch.id}
                  className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                  <ShieldCheck size={12} />{verifyLoading === batch.id ? '...' : t('wh.verify')}
                </button>
              )}
              {batch.status === 'closed' && canReopen && (
                <button onClick={() => handleReopenBatch(batch.id, batch.seller_id)}
                  className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-md hover:bg-yellow-700 flex items-center gap-1">
                  <RotateCcw size={12} />{t('wh.reopenBatch')}
                </button>
              )}
            </div>
            {expandedBatch === batch.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>

          {/* Expanded: Items as CARDS */}
          {expandedBatch === batch.id && (
            <div className="border-t border-[var(--color-border)]">
              {/* Guard alerts */}
              {guardProblems.filter(p => p.batch_id === batch.id).length > 0 && (
                <div className="m-3 p-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-600 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CircleAlert size={18} className="text-red-600" />
                    <span className="text-sm font-bold text-red-800 dark:text-red-200">
                      {guardProblems.filter(p => p.batch_id === batch.id).length} {t('wh.items')} -- NO ENTREGAR
                    </span>
                  </div>
                  {guardProblems.filter(p => p.batch_id === batch.id).map((p: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-700 dark:text-red-300">
                      <span className="font-bold">
                        {p.problem_type === 'order_cancelled' ? '--- CANCELADA' : p.problem_type === 'shipment_cancelled' ? '--- ENVIO CANCEL.' : '!!! RECLAMO'}
                      </span>
                      <EntityLink type="ml_order" value={p.ml_order_id} sellerId={sellerId} />
                      {p.ml_shipment_id && <span className="font-mono">Envío #{p.ml_shipment_id}</span>}
                      {p.buyer_nickname && <span>({p.buyer_nickname})</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Close-scan mini-modal */}
              {closeScanItemId && (
                <div className="m-3 p-3 bg-green-50 dark:bg-green-900/10 border border-green-300 dark:border-green-700 rounded-lg">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">{t('wh.scanToClose')}</p>
                  <form onSubmit={handleCloseScanFromList} className="flex items-center gap-2">
                    <input ref={closeScanRef} type="text" value={closeScanCode}
                      onChange={e => setCloseScanCode(e.target.value)}
                      placeholder={t('wh.scanTracking')}
                      className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg font-mono text-[var(--color-text)] outline-none focus:border-green-500"
                      autoFocus />
                    <button type="submit" disabled={!closeScanCode.trim()}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1">
                      <Check size={14} />{t('wh.addAndClose')}
                    </button>
                    <button type="button" onClick={() => { setCloseScanItemId(null); setCloseScanCode(''); setCloseScanError(''); }}
                      className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"><X size={16} /></button>
                  </form>
                  {closeScanError && <p className="text-xs text-red-500 mt-1">{closeScanError}</p>}
                </div>
              )}

              {loadingItems ? (
                <div className="p-4 text-center text-[var(--color-text-secondary)]">{t('common.loading')}</div>
              ) : batchItems.length === 0 ? (
                <div className="p-4 text-center text-[var(--color-text-secondary)]">{t('wh.noItems')}</div>
              ) : (
                <div className="p-3 space-y-2">
                  {batchItems.map((item: any) => {
                    const problem = guardProblems.find((p: any) => p.ml_order_id === item.ml_order_id && p.batch_id === expandedBatch);
                    const isProblematic = !!problem;
                    return (
                      <div key={item.id}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                          isProblematic
                            ? 'border-l-[6px] border-l-red-500 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 animate-pulse'
                            : 'border-[var(--color-border)] bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)]'
                        }`}>
                        {/* Thumbnail 80x80 */}
                        <div className="relative shrink-0">
                          {item.thumbnail ? (
                            <img src={hiRes(item.thumbnail) || item.thumbnail} alt="" className="w-[80px] h-[80px] rounded-lg object-cover bg-white shadow" />
                          ) : (
                            <div className="w-[80px] h-[80px] bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                              <Package size={32} className="text-gray-400" />
                            </div>
                          )}
                          {(item.quantity || 1) > 1 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
                              {item.quantity}
                            </span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          {/* Row 1: ASIN + Status */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              {item.asin && (
                                <EntityLink type="asin" value={item.asin} sellerId={sellerId} className="text-sm" />
                              )}
                            </div>
                            {isProblematic ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                <CircleAlert size={12} />
                                {problem.problem_type === 'order_cancelled' ? 'CANCELADA' :
                                 problem.problem_type === 'shipment_cancelled' ? 'ENVIO CANCEL.' : 'RECLAMO'}
                              </span>
                            ) : (
                              <div className="flex items-center gap-1">
                                <div className={`w-2.5 h-2.5 rounded-full ${
                                  item.status === 'received' ? 'bg-yellow-500' :
                                  item.status === 'packed' ? 'bg-blue-500' :
                                  item.status === 'closed' ? 'bg-green-500' : 'bg-gray-300'
                                }`} />
                                <span className="text-xs text-[var(--color-text-secondary)] capitalize">{t(`wh.${item.status}`) || item.status}</span>
                              </div>
                            )}
                          </div>

                          {/* Row 2: Full title (NEVER truncated) */}
                          <p className="text-sm text-[var(--color-text)] leading-snug font-medium mb-1">
                            {item.display_title || item.title || '-'}
                          </p>

                          {/* Row 3: Buyer + date */}
                          <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)] mb-1">
                            {item.buyer_nickname && (
                              <span className="flex items-center gap-1">
                                <User size={11} className="shrink-0" />
                                {item.buyer_nickname}
                              </span>
                            )}
                            {item.order_date && (
                              <span>{new Date(item.order_date).toLocaleDateString()}</span>
                            )}
                          </div>

                          {/* Row 4: EntityLinks + source */}
                          <div className="flex items-center gap-3 text-xs flex-wrap">
                            {item.bag_code ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold">
                                <ShoppingBag size={12} className="shrink-0" />
                                Bolsa {item.bag_code}
                              </span>
                            ) : item.no_bag_reason ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-semibold">
                                <ShoppingBag size={12} className="shrink-0" />
                                Sin bolsa ({item.no_bag_reason})
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                                <ShoppingBag size={12} className="shrink-0" />
                                Bolsa sin asignar
                              </span>
                            )}
                            {item.ml_order_id && (
                              <EntityLink type="ml_order" value={item.ml_order_id} sellerId={sellerId} />
                            )}
                            {item.shipment_id && (
                              <EntityLink type="ml_shipment" value={item.shipment_id} sellerId={sellerId} />
                            )}
                            {item.amazon_tracking_number && (
                              <EntityLink type="tracking" value={item.amazon_tracking_number} sellerId={sellerId} />
                            )}
                            <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] text-[10px]">
                              {item.source?.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        {(canOperate || canDeleteItem) && (
                          <div className="flex flex-col items-center gap-1 shrink-0">
                            {item.status === 'received' && canOperate && (
                              <button onClick={() => handlePackItem(item.id)}
                                className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg text-blue-600"
                                title={t('wh.pack')}>
                                <PackageCheck size={18} />
                              </button>
                            )}
                            {(item.status === 'received' || item.status === 'packed') && canOperate && (
                              <button onClick={() => {
                                setCloseScanItemId(item.id); setCloseScanCode(''); setCloseScanError('');
                                setTimeout(() => closeScanRef.current?.focus(), 100);
                              }}
                                className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg text-green-600"
                                title={t('wh.addAndClose')}>
                                <Scan size={18} />
                              </button>
                            )}
                            {canDeleteItem && (
                              <button onClick={() => handleDeleteItem(item.id)}
                                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-500"
                                title={t('common.delete')}>
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
